import { Grid } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useWorkingUnitLocationMetadata, useWorkingUnitLocations } from '../../hooks';
import { Location } from '../../types/Location.interface';
import { WorkingUnitLocationMetadata } from '../../types/WorkingUnitLocation.interface';
import LocationSelect from './LocationSelect';
import WorkingUnitSelect, { WorkingUnitOption } from './WorkingUnitSelect';

export type WorkingUnitLocationSelectProps = {
    locationOptions: Location[];
    selectedLocation: number;
    onSelectedLocationChange: (selectedLocation: number) => void;
    selectedWorkingUnitLocation: number;
    onSelectedWorkingUnitLocationChange: (
        workingUnitLocationId: number,
        workingUnitLocation?: WorkingUnitOption
    ) => void;
    workingUnitHasMatchingOfferedCluster?: boolean;
    dateFrom?: string;
    dateTo?: string;
    includeLatestDataMonth?: boolean;
    includeCorrectedTotalsInfo?: boolean;
    releasedOnly?: boolean;
};

const WorkingUnitLocationSelect = ({
    locationOptions,
    selectedLocation,
    onSelectedLocationChange,
    selectedWorkingUnitLocation,
    onSelectedWorkingUnitLocationChange,
    workingUnitHasMatchingOfferedCluster = false,
    dateFrom,
    dateTo,
    includeLatestDataMonth = false,
    includeCorrectedTotalsInfo = false,
    releasedOnly = false,
}: WorkingUnitLocationSelectProps) => {
    // Base option list. The include* flags now scope the list to WULs that have bidding data (cheap
    // EXISTS on the backend) — the month values themselves come from the async metadata query below.
    const [workingUnitLocations, isLoading, isError] = useWorkingUnitLocations({
        location: selectedLocation,
        hasMatchingOfferedCluster: workingUnitHasMatchingOfferedCluster,
        dateFrom,
        dateTo,
        includeLatestDataMonth,
        includeCorrectedTotalsInfo,
        releasedOnly,
    });

    // Per-WUL latest-data / corrected-totals months, fetched separately and asynchronously so the
    // heavy aggregations never block the dropdown — options render immediately, captions fill in.
    const [metadata] = useWorkingUnitLocationMetadata({
        location: selectedLocation,
        hasMatchingOfferedCluster: workingUnitHasMatchingOfferedCluster,
        releasedOnly,
        enabled: includeLatestDataMonth || includeCorrectedTotalsInfo,
    });

    const metadataById = useMemo(() => {
        const map = new Map<number, WorkingUnitLocationMetadata>();
        metadata.forEach((entry) => map.set(entry.workingUnitLocationId, entry));
        return map;
    }, [metadata]);

    const workingUnitLocationOptions: WorkingUnitOption[] = useMemo(
        () =>
            workingUnitLocations.map((workingUnitLocation) => {
                const meta = metadataById.get(workingUnitLocation.id);
                return {
                    id: workingUnitLocation.workingUnit.id,
                    workingUnitId: workingUnitLocation.workingUnitId,
                    workingUnitLocationId: workingUnitLocation.id,
                    worktypeId: workingUnitLocation.workingUnit.worktypeId,
                    channelId: workingUnitLocation.workingUnit.channelId,
                    taskId: workingUnitLocation.workingUnit.taskId,
                    worktype: workingUnitLocation.workingUnit.worktype,
                    channel: workingUnitLocation.workingUnit.channel,
                    task: workingUnitLocation.workingUnit.task,
                    latestDataMonth: meta?.latestDataMonth,
                    correctedTotalsReleasedThroughMonth: meta?.correctedTotalsReleasedThroughMonth,
                    correctedTotalsDraftThroughMonth: meta?.correctedTotalsDraftThroughMonth,
                };
            }),
        [workingUnitLocations, metadataById]
    );

    // Prevent repeated date adjustment notifications when data loads for already-selected unit
    const dateAdjustedForUnitRef = useRef<number | null>(null);

    const onWorkingUnitLocationChange = useCallback(
        (workingUnitLocationId: number) => {
            const workingUnitLocation = workingUnitLocationOptions.find(
                (wu) => wu.workingUnitLocationId === workingUnitLocationId
            );
            onSelectedWorkingUnitLocationChange(workingUnitLocationId, workingUnitLocation);
        },
        [workingUnitLocationOptions, onSelectedWorkingUnitLocationChange]
    );

    useEffect(() => {
        if (!workingUnitLocationOptions.length) return;

        const existingSelection = workingUnitLocationOptions.find(
            (wu) => wu.workingUnitLocationId === selectedWorkingUnitLocation
        );

        if (!selectedWorkingUnitLocation || !existingSelection) {
            // No selection or selection not found - select first option
            const firstOption = workingUnitLocationOptions[0];
            if (firstOption?.workingUnitLocationId != null) {
                onWorkingUnitLocationChange(firstOption.workingUnitLocationId);
            }
        } else if (
            existingSelection?.latestDataMonth &&
            dateAdjustedForUnitRef.current !== selectedWorkingUnitLocation
        ) {
            // latestDataMonth arrives asynchronously (separate metadata query), so gate the date
            // auto-adjust on its presence rather than firing before it loads. Runs once per selection.
            dateAdjustedForUnitRef.current = selectedWorkingUnitLocation;
            onSelectedWorkingUnitLocationChange(selectedWorkingUnitLocation, existingSelection);
        }
    }, [
        selectedWorkingUnitLocation,
        workingUnitLocationOptions,
        onSelectedWorkingUnitLocationChange,
        onWorkingUnitLocationChange,
    ]);

    return (
        <>
            <Grid item xs={3} xl={2}>
                <LocationSelect
                    selectedLocation={selectedLocation}
                    locationOptions={locationOptions}
                    onChange={onSelectedLocationChange}
                ></LocationSelect>
            </Grid>
            <Grid item xs={3} xl={2}>
                <WorkingUnitSelect
                    valueProp="workingUnitLocationId"
                    selectedWorkingUnit={selectedWorkingUnitLocation}
                    onChange={onWorkingUnitLocationChange}
                    workingUnitOptions={workingUnitLocationOptions}
                    loading={isLoading}
                    error={isError}
                ></WorkingUnitSelect>
            </Grid>
        </>
    );
};

export default WorkingUnitLocationSelect;
